/**
 * O Markdown que a BECA escreve, lido em blocos que a tela monta com React.
 *
 * ## Por que um leitor próprio, e pequeno
 *
 * A BECA foi instruída a usar pouco: parágrafo, lista, **negrito**, tabela.
 * Uma biblioteca de Markdown inteira seria dependência nova para isso — e quase
 * todas montam HTML, que teria de entrar com `dangerouslySetInnerHTML`. Aqui
 * nada vira HTML: o texto vira blocos, os blocos viram elementos React, e um
 * `<script>` escrito pela IA (ou vindo de um nome de empresa cadastrado) aparece
 * como texto. O que este leitor não conhece também aparece como texto.
 */

export type Trecho =
  | { readonly tipo: 'texto'; readonly texto: string }
  | { readonly tipo: 'negrito'; readonly texto: string }
  | { readonly tipo: 'italico'; readonly texto: string }
  | { readonly tipo: 'codigo'; readonly texto: string };

export type Alinhamento = 'esquerda' | 'direita' | 'centro';

export type Bloco =
  /** Cada linha do parágrafo é uma lista de trechos; entre elas, quebra de linha. */
  | { readonly tipo: 'paragrafo'; readonly linhas: readonly (readonly Trecho[])[] }
  | { readonly tipo: 'titulo'; readonly trechos: readonly Trecho[] }
  | {
      readonly tipo: 'lista';
      readonly ordenada: boolean;
      readonly itens: readonly (readonly Trecho[])[];
    }
  | {
      readonly tipo: 'tabela';
      readonly cabecalho: readonly (readonly Trecho[])[];
      readonly linhas: readonly (readonly (readonly Trecho[])[])[];
      readonly alinhamentos: readonly Alinhamento[];
    };

const ITEM_SEM_ORDEM = /^\s*[-*•]\s+(.*)$/;
const ITEM_COM_ORDEM = /^\s*\d+[.)]\s+(.*)$/;
const TITULO = /^#{1,6}\s+(.*)$/;
const SEPARADOR_DE_TABELA = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|?\s*$/;
/** "R$ 1.234,56", "25,5%", "-3", "12/2026"... — coluna assim alinha à direita. */
const PARECE_NUMERO = /^[-+]?(R\$\s?)?[\d.,/]+\s?%?$/;

const marcador = (c: string | undefined): boolean => c === '*' || c === '_';
/** O que pode vir antes de um `*` ou `_` que abre itálico — início, espaço ou pontuação. */
const abreItalico = (antes: string | undefined): boolean =>
  antes === undefined || /[\s(["'—-]/.test(antes);
const fechaItalico = (depois: string | undefined): boolean =>
  depois === undefined || /[\s.,;:!?)\]"'—-]/.test(depois);

/** Negrito, itálico e código dentro de uma linha. `parcelas_em_aberto` continua texto. */
export function lerTrechos(linha: string): Trecho[] {
  const trechos: Trecho[] = [];
  let texto = '';
  const soltarTexto = (): void => {
    if (texto !== '') trechos.push({ tipo: 'texto', texto });
    texto = '';
  };

  let i = 0;
  while (i < linha.length) {
    if (linha.startsWith('**', i)) {
      const fim = linha.indexOf('**', i + 2);
      if (fim > i + 2) {
        soltarTexto();
        trechos.push({ tipo: 'negrito', texto: linha.slice(i + 2, fim) });
        i = fim + 2;
        continue;
      }
    }
    if (linha[i] === '`') {
      const fim = linha.indexOf('`', i + 1);
      if (fim > i + 1) {
        soltarTexto();
        trechos.push({ tipo: 'codigo', texto: linha.slice(i + 1, fim) });
        i = fim + 1;
        continue;
      }
    }
    const m = linha[i];
    if (
      marcador(m) &&
      abreItalico(linha[i - 1]) &&
      linha[i + 1] !== undefined &&
      linha[i + 1] !== ' '
    ) {
      let fim = linha.indexOf(m!, i + 1);
      while (fim !== -1 && !(linha[fim - 1] !== ' ' && fechaItalico(linha[fim + 1]))) {
        fim = linha.indexOf(m!, fim + 1);
      }
      if (fim > i + 1) {
        soltarTexto();
        trechos.push({ tipo: 'italico', texto: linha.slice(i + 1, fim) });
        i = fim + 1;
        continue;
      }
    }
    texto += linha[i];
    i += 1;
  }
  soltarTexto();
  return trechos;
}

function celulas(linha: string): string[] {
  const sem = linha.trim().replace(/^\|/, '').replace(/\|$/, '');
  return sem.split('|').map((c) => c.trim());
}

function alinhamentoDe(separador: string): Alinhamento {
  const s = separador.trim();
  if (s.startsWith(':') && s.endsWith(':')) return 'centro';
  if (s.endsWith(':')) return 'direita';
  return 'esquerda';
}

/** O texto da BECA em blocos. */
export function lerMarkdown(texto: string): Bloco[] {
  const linhas = texto.replace(/\r\n?/g, '\n').split('\n');
  const blocos: Bloco[] = [];
  let paragrafo: Trecho[][] = [];
  let lista: { ordenada: boolean; itens: Trecho[][]; brutos: string[] } | null = null;

  const fecharParagrafo = (): void => {
    if (paragrafo.length > 0) blocos.push({ tipo: 'paragrafo', linhas: paragrafo });
    paragrafo = [];
  };
  const fecharLista = (): void => {
    if (lista !== null) {
      blocos.push({
        tipo: 'lista',
        ordenada: lista.ordenada,
        itens: lista.brutos.map(lerTrechos),
      });
    }
    lista = null;
  };

  for (let i = 0; i < linhas.length; i += 1) {
    const linha = linhas[i]!;

    if (linha.trim() === '') {
      fecharParagrafo();
      fecharLista();
      continue;
    }

    // Tabela: uma linha com "|" seguida do separador "|---|---|".
    if (linha.includes('|') && SEPARADOR_DE_TABELA.test(linhas[i + 1] ?? '')) {
      fecharParagrafo();
      fecharLista();
      const cabecalho = celulas(linha);
      const separadores = celulas(linhas[i + 1]!);
      const corpo: string[][] = [];
      i += 2;
      while (i < linhas.length && linhas[i]!.trim() !== '' && linhas[i]!.includes('|')) {
        corpo.push(celulas(linhas[i]!));
        i += 1;
      }
      i -= 1;
      const colunas = cabecalho.length;
      const linhasDaTabela = corpo.map((c) =>
        Array.from({ length: colunas }, (_, k) => c[k] ?? ''),
      );
      blocos.push({
        tipo: 'tabela',
        cabecalho: cabecalho.map(lerTrechos),
        linhas: linhasDaTabela.map((l) => l.map(lerTrechos)),
        alinhamentos: cabecalho.map((_, k) => {
          const explicito =
            separadores[k] === undefined ? 'esquerda' : alinhamentoDe(separadores[k]!);
          if (explicito !== 'esquerda') return explicito;
          const valores = linhasDaTabela
            .map((l) => l[k]!.replace(/\*\*/g, '').trim())
            .filter((v) => v !== '' && v !== '—' && v !== '-');
          return valores.length > 0 && valores.every((v) => PARECE_NUMERO.test(v))
            ? 'direita'
            : 'esquerda';
        }),
      });
      continue;
    }

    const titulo = TITULO.exec(linha);
    if (titulo !== null) {
      fecharParagrafo();
      fecharLista();
      blocos.push({ tipo: 'titulo', trechos: lerTrechos(titulo[1]!) });
      continue;
    }

    const semOrdem = ITEM_SEM_ORDEM.exec(linha);
    const comOrdem = semOrdem === null ? ITEM_COM_ORDEM.exec(linha) : null;
    const item = semOrdem ?? comOrdem;
    if (item !== null) {
      fecharParagrafo();
      const ordenada = comOrdem !== null;
      if (lista !== null && lista.ordenada !== ordenada) fecharLista();
      lista ??= { ordenada, itens: [], brutos: [] };
      lista.brutos.push(item[1]!);
      continue;
    }

    // Linha comum logo depois de um item: continuação do item.
    if (lista !== null) {
      lista.brutos[lista.brutos.length - 1] += ` ${linha.trim()}`;
      continue;
    }
    paragrafo.push(lerTrechos(linha));
  }
  fecharParagrafo();
  fecharLista();
  return blocos;
}
